import {useFormik} from 'formik'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {KTSVG} from '../../../../../../_gori/helpers'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {InputCheckNoFormik} from '../../../../../../_gori/partials/widgets/form/InputCheckNoFormik'
import {
  Button,
  ConfirmActionSwal,
  InputTextFormik,
  InputTextNoFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import clsx from 'clsx'
import SettingsService from '../../../core/_requests'
import {EmailVerifyResult, ManagerAccount, ManagerFormData} from '../../../core/_types'

type Props = {
  show: boolean
  handleClose: () => void
  reloadTable?: () => void
  isEdit?: boolean
  manager?: ManagerFormData | null
  onDeactivate?: (manager: ManagerFormData) => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const verifyManagerEmail = async (email: string): Promise<EmailVerifyResult> => {
  const response = await SettingsService.verifyEmail({email})
  return {
    new: response.new,
    exists: response.exists,
    invited: response.invited ?? false,
    accounts: response.accounts ?? [],
  }
}

const ManagerModal: React.FC<Props> = ({
  show,
  handleClose,
  reloadTable,
  isEdit = false,
  manager = null,
  onDeactivate,
}) => {
  const intl = useIntl()
  const {infoYup, authYup} = UseYupValidate()
  const [accountSearch, setAccountSearch] = useState('')
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<any>()
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false)
  const [emailVerifyResult, setEmailVerifyResult] = useState<EmailVerifyResult | null>(null)
  const [accounts, setAccounts] = useState<ManagerAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

  const initialValues = useMemo(
    () => ({
      email: manager?.email ?? '',
      branch_name: manager?.branch?.name ?? '',
    }),
    [manager]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: infoYup.email('EMAIL'),
        branch_name: authYup.companyName(50, 'BRANCH_NAME'),
      }),
    [infoYup, authYup]
  )

  const onSubmit = async (values) => {
    if (selectedAccountIds.length === 0) {
      toast.error(intl.formatMessage({id: 'PLEASE_SELECT_AT_LEAST_ONE_ACCOUNT'}))
      return
    }

    setIsSubmitting(true)
    try {
      if (!isEdit) {
        const {message} = await SettingsService.createManager({
          email: values.email.trim(),
          branch_name: values.branch_name.trim(),
          account_ids: selectedAccountIds,
        })

        toast.success(
          intl.formatMessage({
            id: message,
          })
        )
      } else {
        const {message} = await SettingsService.updateManager(manager?.branch?.id, {
          account_ids: selectedAccountIds,
        })

        toast.success(
          intl.formatMessage({
            id: message,
          })
        )
      }

      reloadTable?.()
      handleCloseModal()
    } catch (error: any) {
      setValidationErrors(error?.response)
    } finally {
      setIsSubmitting(false)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  })

  const {resetForm} = formik

  useEffect(() => {
    if (!show || isEdit) {
      setEmailVerifyResult(null)
      return
    }

    const email = formik.values.email?.trim().toLowerCase()
    if (!email || !EMAIL_PATTERN.test(email)) {
      setEmailVerifyResult(null)
      setAccounts([])
      setIsLoadingAccounts(false)
      return
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      setIsLoadingAccounts(true)
      try {
        const result = await verifyManagerEmail(email)
        if (!cancelled) {
          setEmailVerifyResult(result)
          setAccounts(result.accounts)
        }
      } catch {
        if (!cancelled) {
          setEmailVerifyResult(null)
          setAccounts([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccounts(false)
        }
      }
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(timer)
      setIsLoadingAccounts(false)
    }
  }, [formik.values.email, isEdit, show])

  useEffect(() => {
    if (!show || !isEdit || !manager?.branch?.id) return

    let cancelled = false
    const fetchEditManagerData = async () => {
      setIsLoadingAccounts(true)
      try {
        const detailResponse = await SettingsService.getManagerDetail(manager.branch.id)
        if (cancelled) return

        setSelectedAccountIds((detailResponse.accounts ?? []).map((account) => account.id))

        if (manager.email) {
          const {accounts: assignableAccounts} = await verifyManagerEmail(manager.email)
          if (!cancelled) {
            setAccounts(assignableAccounts)
          }
        } else {
          setAccounts(
            (detailResponse.accounts ?? []).map(({id, name}) => ({
              id,
              name,
            }))
          )
        }
      } catch {
        if (!cancelled) {
          setAccounts([])
          setSelectedAccountIds([])
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAccounts(false)
        }
      }
    }
    fetchEditManagerData()

    return () => {
      cancelled = true
    }
  }, [show, isEdit, manager?.branch?.id, manager?.email])

  const handleCloseModal = useCallback(() => {
    resetForm()
    setAccountSearch('')
    setSelectedAccountIds([])
    setValidationErrors(undefined)
    setShowDeactivateConfirm(false)
    setEmailVerifyResult(null)
    setAccounts([])
    setIsLoadingAccounts(false)
    handleClose()
  }, [resetForm, handleClose])

  const filteredAccounts = useMemo(() => {
    const query = accountSearch.trim().toLowerCase()
    if (!query) return accounts
    return accounts.filter((account) => account.name?.toLowerCase().includes(query) ?? false)
  }, [accountSearch, accounts])

  const selectedAccounts = useMemo(
    () => accounts.filter((account) => selectedAccountIds.includes(account.id)),
    [selectedAccountIds, accounts]
  )

  const isAlreadyManager = useMemo(
    () =>
      !isEdit &&
      !!emailVerifyResult?.invited &&
      emailVerifyResult.new === false &&
      !!emailVerifyResult.exists,
    [isEdit, emailVerifyResult]
  )

  const isEmailNotAssociated = useMemo(
    () => !isEdit && emailVerifyResult?.new === false && !emailVerifyResult.exists,
    [isEdit, emailVerifyResult]
  )

  const isAssignAccountsDisabled = isAlreadyManager || isEmailNotAssociated || isLoadingAccounts

  const toggleAccount = (accountId: number) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    )
  }

  const removeAccount = (accountId: number) => {
    setSelectedAccountIds((prev) => prev.filter((id) => id !== accountId))
  }

  const handleConfirmDeactivate = useCallback(() => {
    if (!manager) return
    onDeactivate?.(manager)
    if (!onDeactivate) {
      toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
    }
    handleCloseModal()
  }, [manager, onDeactivate, intl, handleCloseModal])

  return (
    <>
      <ConfirmActionSwal
        show={showDeactivateConfirm}
        title={intl.formatMessage({id: 'DEACTIVATE'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleConfirmDeactivate}
        handleClose={() => setShowDeactivateConfirm(false)}
      />
      {validationErrors && (
        <ValidationErrorModal
          response={validationErrors}
          handleClose={() => setValidationErrors(undefined)}
        />
      )}
      <Modal
        id={isEdit ? 'gori_modal_edit_manager' : 'gori_modal_add_manager'}
        tabIndex={-1}
        centered
        dialogClassName='modal-size-lg h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix='fw-bolder fs-1 modal-title'>
            {intl.formatMessage({id: isEdit ? 'EDIT_MANAGER' : 'ADD_MANAGER'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='overflow-auto'>
          <div className='bg-light rounded px-6 py-6 mb-6'>
            <h5 className='fw-bolder mb-5 card-title modal-section-title'>
              {intl.formatMessage({id: 'MANAGER_INFORMATION'})}
            </h5>
            <div className='row g-5'>
              <div className='d-flex flex-wrap col-md-12'>
                <InputTextFormik
                  className='col-12 col-md-9'
                  labelClassName='col-form-label text-nowrap col-md-3 fs-6'
                  required
                  label={intl.formatMessage({id: 'EMAIL'})}
                  placeholder={intl.formatMessage({id: 'EMAIL'})}
                  formik={formik}
                  name='email'
                  disabled={isSubmitting || isEdit}
                >
                  {!isEdit && isAlreadyManager && (
                    <div className='manager-email-existing-notice d-flex align-items-center'>
                      <div className='flex-shrink-0'>
                        <KTSVG
                          path='/media/gori/settings/user-check.svg'
                          className='svg-icon svg-icon-2'
                        />
                      </div>
                      <span className='manager-email-existing-notice__text'>
                        {intl.formatMessage({
                          id: 'THIS_USER_IS_ALREADY_A_MANAGER_IN_THIS_ORGANIZATION',
                        })}
                      </span>
                    </div>
                  )}
                  {!isEdit &&
                    !emailVerifyResult?.invited &&
                    emailVerifyResult?.new === false &&
                    emailVerifyResult.exists && (
                      <div className='manager-email-existing-notice d-flex align-items-center'>
                        <div className='flex-shrink-0'>
                          <KTSVG
                            path='/media/gori/settings/user-check.svg'
                            className='svg-icon svg-icon-2'
                          />
                        </div>
                        <span className='manager-email-existing-notice__text'>
                          {intl.formatMessage({id: 'EMAIL_LINKED_TO_EXISTING_SHIPBAE_ACCOUNT'})}
                        </span>
                      </div>
                    )}
                  {isEmailNotAssociated && (
                    <div className='manager-email-not-associated-notice d-flex align-items-center'>
                      <div className='flex-shrink-0'>
                        <KTSVG
                          path='/media/gori/settings/user-check.svg'
                          className='svg-icon svg-icon-2'
                        />
                      </div>
                      <span className='manager-email-not-associated-notice__text'>
                        {intl.formatMessage({
                          id: 'THIS_EMAIL_IS_NOT_ASSOCIATED_TO_THIS_ORGANIZATION',
                        })}
                      </span>
                    </div>
                  )}
                </InputTextFormik>
              </div>
              <div className='d-flex flex-wrap col-md-12'>
                <InputTextFormik
                  className='col-12 col-md-9'
                  labelClassName='col-form-label text-nowrap col-md-3 fs-6'
                  required
                  label={intl.formatMessage({id: 'BRANCH_NAME'})}
                  formik={formik}
                  name='branch_name'
                  placeholder={intl.formatMessage({id: 'BRANCH_NAME'})}
                  disabled={isSubmitting || isEdit}
                />
              </div>
            </div>
          </div>

          <div className='bg-light rounded px-6 py-6'>
            <h5 className='fw-bolder mb-5 modal-section-title'>
              {intl.formatMessage({id: 'ASSIGN_ACCOUNTS'})}
            </h5>
            <div className='bg-white border rounded ps-3 mb-4'>
              <div className='d-flex flex-wrap align-items-center gap-2 py-3'>
                <KTSVG
                  path='/media/gori/magnify.svg'
                  className='svg-icon svg-icon-2 flex-shrink-0'
                />
                {selectedAccounts.map((account) => (
                  <div
                    key={account.id}
                    className='filter-selected-badge badge d-flex justify-content-center align-items-center rounded-pill fs-7 fw-bold text-gray-800'
                  >
                    <span className='filter-selected-badge__text'>{account.name}</span>
                    <Button
                      className='btn btn-sm btn-icon p-0 border-0 bg-transparent justify-content-end filter-selected-badge__remove-btn'
                      aria-label={intl.formatMessage({id: 'REMOVE'})}
                      event={() => removeAccount(account.id)}
                      disabled={isSubmitting || isAssignAccountsDisabled}
                    >
                      <KTSVG path='/media/gori/a1/close.svg' className='svg-icon svg-icon-4' />
                    </Button>
                  </div>
                ))}
                <InputTextNoFormik
                  type='text'
                  className='manager-account-search flex-grow-1 min-w-100px fs-7 text-gray-700 h-16px'
                  inputClassName={clsx('border-0 p-0', {
                    'cursor-no-drop': isSubmitting || isAssignAccountsDisabled,
                  })}
                  placeholder={intl.formatMessage({id: 'SEARCH_ACCOUNTS'})}
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  disabled={isSubmitting || isAssignAccountsDisabled}
                />
              </div>
            </div>
            <div className='bg-white border rounded h-200px overflow-auto'>
              {isLoadingAccounts ? (
                <div className='d-flex align-items-center justify-content-center h-100 p-4'>
                  <span
                    className='spinner-border spinner-border-sm text-primary'
                    role='status'
                    aria-label={intl.formatMessage({id: 'LOADING'})}
                  />
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className='d-flex align-items-center justify-content-center h-100 p-4 text-muted fs-7 text-center'>
                  {intl.formatMessage({
                    id: accountSearch.trim() ? 'NO_ACCOUNTS_MATCH_SEARCH' : 'NO_ACCOUNTS_TO_ASSIGN',
                  })}
                </div>
              ) : (
                filteredAccounts.map((account) => (
                  <div
                    key={account.id}
                    className='d-flex align-items-center text-muted filtered-account-item'
                  >
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <InputCheckNoFormik
                        type='checkbox'
                        className={clsx('pe-4', {
                          'cursor-no-drop': isSubmitting || isAssignAccountsDisabled,
                        })}
                        inputClassName='w-20px h-20px'
                        checked={selectedAccountIds.includes(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        disabled={isSubmitting || isAssignAccountsDisabled}
                      />
                    </div>
                    <label className='fw-bold filtered-account-item__text'>{account.name}</label>
                  </div>
                ))
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div
            className={clsx('d-flex align-items-center w-100', {
              'justify-content-between': isEdit,
              'justify-content-end': !isEdit,
            })}
          >
            {isEdit && (
              <div className='fw-bold d-flex'>
                <div
                  className='fs-5 cursor-pointer fw-bolder text-danger'
                  onClick={() => setShowDeactivateConfirm(true)}
                >
                  {intl.formatMessage({id: 'DEACTIVATE'})}
                </div>
              </div>
            )}
            <div className='d-flex justify-content-end'>
              <Button
                className='btn btn-advanced-secondary me-2'
                label={intl.formatMessage({id: 'CANCEL'})}
                loadingText={intl.formatMessage({id: 'CANCEL'})}
                event={handleCloseModal}
                disabled={isSubmitting}
              />
              <Button
                className='btn btn-advanced-primary'
                label={intl.formatMessage({
                  id: isEdit ? 'UPDATE_MANAGER_SETTINGS' : 'ADD_MANAGER',
                })}
                loadingText={intl.formatMessage({
                  id: isEdit ? 'UPDATE_MANAGER_SETTINGS' : 'ADD_MANAGER',
                })}
                disabled={isSubmitting || isAssignAccountsDisabled}
                loading={isSubmitting}
                event={formik.handleSubmit}
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {ManagerModal}
